import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { APP_TIMEZONE } from '../../config';
import { getApprovalType } from './approvalTypes';
import StepTrail from './StepTrail';

const STEP_NAMES = { admin: 'the Admin', council: 'council', residents: 'residents' };
const DECIDED_LABELS = { approved: 'Approved', rejected: 'Rejected', returned: 'Sent back' };

// Where "View {kind}" links to for a decided approval with a result.
function resultLink(approval) {
  const result = approval.result;
  const condoSlug = approval.condo?.slug;
  if (!result || !condoSlug) return null;
  if (result.kind === 'ticket') return `/${condoSlug}/tickets?ticket=${result.id}`;
  if (result.kind === 'bulletin') return `/${condoSlug}/bulletins`;
  if (result.kind === 'calendarEvent') return `/${condoSlug}/calendar`;
  if (result.kind === 'forumPost') return `/${condoSlug}/forum/${result.id}`;
  return null;
}

function ApprovalCard({ approval, onApprove, onReject, onReturn, onSave, busy }) {
  const entry = getApprovalType(approval.type);
  const [editing, setEditing] = useState(false);
  const [formState, setFormState] = useState(null);
  const [saving, setSaving] = useState(false);

  const isDecided = approval.status !== 'pending';
  const steps = approval.steps;
  const currentStep = steps ? steps[approval.currentStep] : null;
  const atAdminStep = !steps || currentStep.kind === 'admin';
  const nextStep = steps && atAdminStep ? steps[approval.currentStep + 1] : null;
  const fromAgent = approval.origin?.kind === 'agent';
  const rejectedNote = steps?.find(step => step.status === 'rejected' && step.note)?.note;
  const Body = entry.View;
  const Form = entry.EditForm;
  const badges = entry.badges(approval);
  const link = isDecided ? resultLink(approval) : null;

  const startEditing = () => {
    setFormState(entry.toFormState(approval.payload));
    setEditing(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(approval._id, entry.toPayload(formState));
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-background border border-border rounded-lg p-4">
      {editing && Form ? (
        <form onSubmit={handleSave} className="space-y-3">
          <Form value={formState} onChange={setFormState} />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving}
              className="bg-primary text-primary-foreground px-4 py-2 rounded text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="bg-muted text-muted-foreground px-4 py-2 rounded text-sm hover:bg-muted/80"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="flex flex-col lg:flex-row lg:items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className="text-lg">{entry.icon}</span>
              <h5 className="font-bold text-foreground">{entry.title(approval)}</h5>
              {badges.map((badge, i) => (
                <span key={i} className={`text-xs px-2 py-0.5 rounded ${badge.className}`}>{badge.text}</span>
              ))}
              {approval.test && (
                <span className="text-xs px-2 py-0.5 rounded bg-warning/20 text-warning">Test</span>
              )}
              {approval.condo && (
                <span className="text-xs text-muted-foreground">{approval.condo.name}</span>
              )}
            </div>
            {steps && <div className="mt-1 mb-2"><StepTrail approval={approval} /></div>}
            <Body approval={approval} />
            {(approval.notes || []).length > 0 && (
              <div className="mt-2 text-sm">
                <p className="text-xs text-muted-foreground">Questions from council</p>
                {approval.notes.map((note, i) => <p key={i} className="text-foreground">{note.byName || 'Council'}: {note.text}</p>)}
              </div>
            )}
            {approval.status === 'returned' && approval.feedback && (
              <p className="mt-2 text-sm text-warning">Sent back{approval.feedback.byName ? ` by ${approval.feedback.byName}` : ''}: {approval.feedback.text}</p>
            )}
            {approval.status === 'rejected' && rejectedNote && (
              <p className="mt-2 text-sm text-muted-foreground">Reason: {rejectedNote}</p>
            )}
            <div className="mt-2 text-xs text-muted-foreground">
              {isDecided ? (
                <>
                  {DECIDED_LABELS[approval.status] || approval.status} by {approval.decidedBy?.name || approval.feedback?.byName || 'Unknown'}
                  {' · '}
                  {new Date(approval.decidedAt).toLocaleString('en-US', { timeZone: APP_TIMEZONE })}
                  {link && (
                    <>
                      {' · '}
                      <Link to={link} className="text-primary hover:underline">View {entry.label.toLowerCase()}</Link>
                    </>
                  )}
                </>
              ) : (
                <>Received: {new Date(approval.createdAt).toLocaleString('en-US', { timeZone: APP_TIMEZONE })}</>
              )}
            </div>
          </div>
          {!isDecided && !atAdminStep && (
            <p className="text-sm text-muted-foreground shrink-0">Waiting for {STEP_NAMES[currentStep.kind] || currentStep.kind}</p>
          )}
          {!isDecided && atAdminStep && (
            <div className="flex flex-wrap gap-2 shrink-0">
              {Form && (
                <button
                  onClick={startEditing}
                  disabled={!!busy}
                  className="text-primary hover:text-primary/80 px-4 py-2 rounded border border-primary hover:bg-primary/10 text-sm transition-colors disabled:opacity-50"
                >
                  Edit
                </button>
              )}
              <button
                onClick={() => onApprove(approval._id)}
                disabled={!!busy}
                className="bg-primary text-primary-foreground px-4 py-2 rounded text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {busy === 'approve' ? 'Approving...' : nextStep ? `Approve, send to ${STEP_NAMES[nextStep.kind] || nextStep.kind}` : 'Approve'}
              </button>
              {steps && fromAgent && onReturn && (
                <button
                  onClick={() => onReturn(approval._id)}
                  disabled={!!busy}
                  className="text-foreground px-4 py-2 rounded border border-border hover:bg-muted text-sm transition-colors disabled:opacity-50"
                >
                  {busy === 'return' ? 'Sending back...' : 'Ask agent to revise'}
                </button>
              )}
              <button
                onClick={() => onReject(approval._id, entry.title(approval), { reasonRequired: !!steps && fromAgent })}
                disabled={!!busy}
                className="text-destructive hover:text-destructive/80 px-4 py-2 rounded border border-destructive hover:bg-destructive/10 text-sm transition-colors disabled:opacity-50"
              >
                {busy === 'reject' ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ApprovalCard;
